import { Icon } from "@iconify/react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useState, useRef } from "react";
import editFill from "@iconify/icons-eva/edit-fill";
import trashFill from "@iconify/icons-eva/trash-2-outline";
import moreVerticalFill from "@iconify/icons-eva/more-vertical-fill";
import {
  doc as firestoreDoc,
  updateDoc as firestoreUpdateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../../Firebase/firebase";
// material
import {
  Menu,
  MenuItem,
  IconButton,
  ListItemIcon,
  ListItemText,
  TextField,
} from "@mui/material";

// ----------------------------------------------------------------------

// Modal Styles
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 400,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "5px",
  boxShadow: 24,
  p: 4,
  display: "flex",
  flexDirection: "column",
};

export default function UserMoreMenu({
  uid,
  trackingId,
  productName,
  customerName,
  orderDate,
  trackingStops: existingTrackingStops = [],
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState({
    productName: "",
    customerName: "",
    orderDate: "",
  });
  const [trackingStops, setTrackingStops] = useState([]);
  const ref = useRef(null);

  const handleEditOpen = () => {
    setEditProduct({
      productName: productName,
      customerName: customerName,
      orderDate: orderDate,
    });
    setTrackingStops(existingTrackingStops || []); // Load existing tracking stops
    setEditModalOpen(true);
    setIsOpen(false);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setEditProduct({
      productName: "",
      customerName: "",
      orderDate: "",
    });
    setTrackingStops([]);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Helper functions for datetime formatting
  const formatDateTimeForInput = (dateTimeString) => {
    // Convert "30th Sep, 2025, 14:00" back to "2025-09-30T14:00" for datetime-local input
    if (!dateTimeString || typeof dateTimeString !== "string") return "";

    // Parse the formatted string and convert to ISO format for datetime-local
    const parts = dateTimeString.match(
      /(\d+)(?:st|nd|rd|th)?\s+(\w+),\s+(\d+),\s+(\d+):(\d+)/
    );
    if (!parts) return "";

    const [, day, month, year, hour, minute] = parts;
    const monthNames = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    };

    const monthNum = monthNames[month];
    if (!monthNum) return "";

    return `${year}-${monthNum}-${day.padStart(2, "0")}T${hour}:${minute}`;
  };

  const formatDateTimeForDisplay = (dateTimeValue) => {
    // Convert "2025-09-30T14:00" to "30th Sep, 2025, 14:00"
    if (!dateTimeValue) return "";

    const date = new Date(dateTimeValue);
    if (isNaN(date.getTime())) return "";

    const day = date.getDate();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hour = date.getHours().toString().padStart(2, "0");
    const minute = date.getMinutes().toString().padStart(2, "0");

    // Add ordinal suffix to day
    const suffix =
      day === 1 || day === 21 || day === 31
        ? "st"
        : day === 2 || day === 22
        ? "nd"
        : day === 3 || day === 23
        ? "rd"
        : "th";

    return `${day}${suffix} ${month}, ${year}, ${hour}:${minute}`;
  };

  const handleTrackingStopInputChange = (index, field, value) => {
    setTrackingStops((prev) =>
      prev.map((stop, i) => (i === index ? { ...stop, [field]: value } : stop))
    );
  };

  const handleAddTrackingStop = () => {
    setTrackingStops((prev) => [
      ...prev,
      { activity: "", datetime: "", location: "" },
    ]);
  };

  const handleRemoveTrackingStop = (index) => {
    setTrackingStops((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditProduct = async () => {
    if (
      !editProduct.productName ||
      !editProduct.customerName ||
      !editProduct.orderDate
    ) {
      alert("Please fill in all required fields");
      return;
    }

    // Validate tracking stops
    if (trackingStops.length === 0) {
      alert("Please add at least one tracking stop");
      return;
    }

    // Validate each tracking stop has all required fields
    for (let i = 0; i < trackingStops.length; i++) {
      const stop = trackingStops[i];
      if (!stop.activity || !stop.datetime || !stop.location) {
        alert(`Please fill in all fields for Tracking Stop ${i + 1}`);
        return;
      }
    }

    try {
      const productRef = firestoreDoc(db, "products", uid);
      await firestoreUpdateDoc(productRef, {
        productName: editProduct.productName,
        customerName: editProduct.customerName,
        orderDate: editProduct.orderDate,
        trackingStops: trackingStops,
      });

      handleEditClose();
      alert("Product updated successfully!");
      window.location.reload();
    } catch (error) {
      console.error("Error updating product:", error);
      alert("Failed to update product. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteDoc(firestoreDoc(db, "products", uid));
        alert("Product deleted successfully!");
        window.location.reload();
      } catch (error) {
        console.error("Error deleting product:", error);
        alert("Failed to delete product. Please try again.");
      }
    }
  };

  return (
    <>
      <IconButton ref={ref} onClick={() => setIsOpen(true)}>
        <Icon icon={moreVerticalFill} width={20} height={20} />
      </IconButton>

      <Menu
        open={isOpen}
        anchorEl={ref.current}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: { width: 200, maxWidth: "100%" },
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleEditOpen} sx={{ color: "text.secondary" }}>
          <ListItemIcon>
            <Icon icon={editFill} width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary="Edit"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </MenuItem>

        <MenuItem onClick={handleDelete} sx={{ color: "text.secondary" }}>
          <ListItemIcon>
            <Icon icon={trashFill} width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </MenuItem>
      </Menu>

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={editModalOpen}
        onClose={handleEditClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={editModalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: 400,
              bgcolor: "background.paper",
              border: "none",
              borderRadius: "5px",
              boxShadow: 24,
              p: 4,
              maxHeight: "90vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" component="h2" gutterBottom>
              Edit Product
            </Typography>

            <TextField
              label="Product Name"
              name="productName"
              value={editProduct.productName}
              onChange={handleEditInputChange}
              fullWidth
              margin="normal"
              required
            />

            <TextField
              label="Customer Name"
              name="customerName"
              value={editProduct.customerName}
              onChange={handleEditInputChange}
              fullWidth
              margin="normal"
              required
            />

            <TextField
              label="Order Date"
              name="orderDate"
              value={editProduct.orderDate}
              onChange={handleEditInputChange}
              fullWidth
              margin="normal"
              required
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
            />

            {/* Tracking Stop Sections */}
            {trackingStops.map((stop, index) => (
              <Box
                key={index}
                sx={{
                  mt: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Tracking Stop {index + 1}
                </Typography>
                <TextField
                  label="Activity"
                  value={stop.activity}
                  onChange={(e) =>
                    handleTrackingStopInputChange(
                      index,
                      "activity",
                      e.target.value
                    )
                  }
                  fullWidth
                  margin="normal"
                  placeholder="e.g., Package Picked Up"
                />
                <TextField
                  label="Date & Time"
                  type="datetime-local"
                  value={
                    stop.datetime ? formatDateTimeForInput(stop.datetime) : ""
                  }
                  onChange={(e) =>
                    handleTrackingStopInputChange(
                      index,
                      "datetime",
                      formatDateTimeForDisplay(e.target.value)
                    )
                  }
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  label="Location"
                  value={stop.location}
                  onChange={(e) =>
                    handleTrackingStopInputChange(
                      index,
                      "location",
                      e.target.value
                    )
                  }
                  fullWidth
                  margin="normal"
                  placeholder="e.g., New York, NY"
                />
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleRemoveTrackingStop(index)}
                  sx={{ mt: 1 }}
                >
                  Remove Stop
                </Button>
              </Box>
            ))}

            {/* Add Tracking Stop Button */}
            <Box
              sx={{
                mt: 2,
                mb: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleAddTrackingStop}
                fullWidth
                startIcon={
                  <IconButton sx={{ color: "success.dark" }}>
                    <Icon icon="eva:plus-fill" width={14} height={14} />
                  </IconButton>
                }
                sx={{ color: "success.dark", textTransform: "none" }}
              >
                Add Stop
              </Button>
            </Box>

            <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
              <Button variant="contained" onClick={handleEditProduct} fullWidth>
                Add Shipping
              </Button>
              <Button variant="outlined" onClick={handleEditClose} fullWidth>
                Cancel
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
}
